import {
  EditActionDefinition,
  EditActionsDictionary,
  ExecutionMethodsList,
} from '@wix/editor-platform-host-integration-apis';
import { ACTIONS } from '@wix/component-protocol/schema';

import { platformEvents } from '../../../../platformEvents';
import { getComponentAppData } from '../../componentsService/utils';

import {
  ActionFactoryWithPlatformContext,
  PlatformContext,
  CustomManifestAction,
  ExtendedPlatformContext,
} from '../types';
import { ActionExecutionStore } from '../actionExecutionStore';
import {
  createEditorPlatformEditActionsError,
  EditorPlatformEditActionsErrorCode,
} from '../errors';
import { ForwardAction } from './forwardAction';
import { objectMap } from '../utils';

// This function is required for the backward compatibility with the GFPP events supported by the old SDK version
const widgetGfppEventClicked = (
  actionName: string,
  action: CustomManifestAction,
  context: PlatformContext,
) => {
  const { editorPointer, documentServicesAPI, editorPointerAPI } = context;

  const { appDefinitionId } = getComponentAppData(
    editorPointer,
    documentServicesAPI,
    editorPointerAPI,
  );

  if (!appDefinitionId) {
    return;
  }

  documentServicesAPI.platform.notifyApplicationByAppDefId(
    appDefinitionId,
    platformEvents.factory.widgetGfppClicked({
      id: action.execution?.event?.event || actionName,
      componentRef: editorPointerAPI.getDocumentPointer(editorPointer),
      gfppOrigin: 'gfpp',
    }),
  );
};

const executeWithActionType = (
  actionName: string,
  action: CustomManifestAction,
  context: ExtendedPlatformContext,
) => {
  const customisationAPI = context.customisationAPI;

  if (!customisationAPI.hasCustomisation() || !action?.execution) {
    return;
  }

  switch (customisationAPI.apiType) {
    case ACTIONS.ACTION_TYPE.event:
      const fireEventFn = customisationAPI.getCustomisation();
      fireEventFn();
      break;
    case ACTIONS.ACTION_TYPE.data:
    case ACTIONS.ACTION_TYPE.cssCustomProperty:
    case ACTIONS.ACTION_TYPE.cssProperty:
    case ACTIONS.ACTION_TYPE.displayGroup:
      ActionExecutionStore.getExecutionMethod(ExecutionMethodsList.codePanel)?.(
        { panelContent: customisationAPI.getCustomisation().panelContent },
        action?.displayName!,
      );
      break;
    case ACTIONS.ACTION_TYPE.panel:
      if (
        customisationAPI.panelContentType === ACTIONS.PANEL_CONTENT_TYPE.url
      ) {
        const framePanelProps = customisationAPI.getCustomisation();
        ActionExecutionStore.getExecutionMethod(
          ExecutionMethodsList.urlPanel,
        )?.(framePanelProps, action?.displayName!);
      } else if (
        customisationAPI.panelContentType === ACTIONS.PANEL_CONTENT_TYPE.code
      ) {
        ActionExecutionStore.getExecutionMethod(
          ExecutionMethodsList.codePanel,
        )?.(customisationAPI.getCustomisation(), action?.displayName!);
      }
      break;
    case ACTIONS.ACTION_TYPE.forward:
      const forwardAction = new ForwardAction(action, actionName, context);
      const target = forwardAction.getTarget();

      if (forwardAction.isExecutable(target.action)) {
        target.action.execute();
        break;
      }

      executeWithActionType(target.actionName, target.action, target.context);

      break;
    default:
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionUnsupportedActionType,
        `Unsupported action type ${customisationAPI.apiType} for action ${actionName}`,
      ).withActionName(actionName);
  }
};

const createCustomActionExecute =
  (
    actionName: string,
    action: CustomManifestAction,
    context: ExtendedPlatformContext,
  ) =>
  () => {
    widgetGfppEventClicked(actionName, action, context);
    executeWithActionType(actionName, action, context);
  };

export const convertCustomActionToExecutableFactory = (
  customActions: EditActionsDictionary<CustomManifestAction>,
): Record<string, ActionFactoryWithPlatformContext> =>
  objectMap(
    customActions,
    ({ execution, ...action }, actionName) =>
      (context) =>
        ({
          ...action,
          actionName,
          execute: createCustomActionExecute(
            actionName,
            { execution, ...action } as CustomManifestAction,
            context,
          ),
        } as EditActionDefinition),
  );
